"use client";

import { useState, useEffect } from "react";
import { TREATMENTS, calcClinicDuration } from "./treatments";
import { track } from "@lib/track";
import MarketingOptIn from "@/app/components/MarketingOptIn";

type ClinicDate = { day: string; month: string; year: string; iso: string };

interface Props {
  upcomingDates: ClinicDate[];
}

export default function ClinicBookingClient({ upcomingDates }: Props) {
  const [selectedTreatments, setSelectedTreatments] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlotRaw, setSelectedSlotRaw] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const total = selectedTreatments.reduce((sum, t) => {
    const entry = TREATMENTS.find((x) => x.name === t);
    return sum + (entry?.price ?? 0);
  }, 0);

  const duration = calcClinicDuration(selectedTreatments);
  const durationLabel = duration === 15 ? "15-minute slot" : "1-hour slot";

  const imTreatments = TREATMENTS.filter((t) => t.type === "IM");
  const ivTreatments = TREATMENTS.filter((t) => t.type === "IV");

  function toggleTreatment(name: string) {
    setSelectedTreatments((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
    // Reset downstream selections when treatment list changes
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);
  }

  useEffect(() => {
    if (!selectedDate) return;

    setSlotsLoading(true);
    setSlots([]);
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);

    fetch("/api/dayclinic/availability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date: selectedDate, duration }),
    })
      .then((r) => r.json())
      .then((data) => setSlots(Array.isArray(data.slots) ? data.slots : []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, duration]);

  function selectSlot(raw: string) {
    setSelectedSlotRaw(raw);
    // Convert "9:00AM" → "09:00"
    const match = raw.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
    if (!match) return;
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    else if (period === "AM" && h === 12) h = 0;
    setSelectedStartTime(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;

    if (selectedTreatments.length === 0) {
      alert("Please select at least one treatment.");
      return;
    }
    if (!selectedDate || !selectedSlotRaw || !selectedStartTime) {
      alert("Please select a date and time slot.");
      return;
    }
    if (!name.trim()) {
      alert("Please enter your name.");
      return;
    }
    if (!email.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      track(
        "dayclinic_booking_submit",
        { label: `${selectedDate} ${selectedSlotRaw} | ${email}`, component: "ClinicBookingClient" },
        { event: "dayclinic_booking_submit", label: `${selectedDate} ${selectedSlotRaw}`, component: "ClinicBookingClient" }
      );
    } catch {}

    try {
      const res = await fetch("/api/dayclinic/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: selectedDate,
          startTime: selectedStartTime,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          treatments: selectedTreatments,
          marketingOptIn,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error ?? "Failed to initiate booking");
      }
      window.location.href = data.paymentUrl;
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const friendlyDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="rounded-2xl bg-[var(--bg-card)] border border-[var(--border-on-light)] overflow-hidden">

      {/* Step 1 — Treatments */}
      <div className="p-6 md:p-8 border-b border-[var(--border-on-light)]">
        <h3 className="text-base font-semibold text-[var(--text-brand)] mb-1 uppercase tracking-wider">
          Step 1 — Select your treatments
        </h3>
        <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
          Refer to the pricing menu above for combination notes. You can book multiple treatments in one slot.
        </p>

        <p className="text-xs font-semibold text-[var(--text-on-light)] uppercase tracking-wider mb-2">
          Intramuscular (IM)
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mb-5">
          {imTreatments.map((t) => (
            <label
              key={t.name}
              className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors text-sm
                ${selectedTreatments.includes(t.name)
                  ? "border-[var(--text-brand)] bg-[var(--bg-brand-tint)] text-[var(--text-on-light)]"
                  : "border-[var(--border-on-light)] bg-[var(--bg-surface)] text-[var(--text-on-light)] hover:border-[var(--text-brand)]"}`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-[var(--text-brand)] shrink-0"
                  checked={selectedTreatments.includes(t.name)}
                  onChange={() => toggleTreatment(t.name)}
                />
                {t.name}
              </span>
              <span className="font-semibold shrink-0">£{t.price}</span>
            </label>
          ))}
        </div>

        <p className="text-xs font-semibold text-[var(--text-on-light)] uppercase tracking-wider mb-2">
          Intravenous (IV)
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {ivTreatments.map((t) => (
            <label
              key={t.name}
              className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors text-sm
                ${selectedTreatments.includes(t.name)
                  ? "border-[var(--text-brand)] bg-[var(--bg-brand-tint)] text-[var(--text-on-light)]"
                  : "border-[var(--border-on-light)] bg-[var(--bg-surface)] text-[var(--text-on-light)] hover:border-[var(--text-brand)]"}`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-[var(--text-brand)] shrink-0"
                  checked={selectedTreatments.includes(t.name)}
                  onChange={() => toggleTreatment(t.name)}
                />
                {t.name}
              </span>
              <span className="font-semibold shrink-0">£{t.price}</span>
            </label>
          ))}
        </div>

        {selectedTreatments.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-lg bg-[var(--bg-surface)] border border-[var(--border-on-light)] px-4 py-3">
            <span className="text-sm text-[var(--text-on-light-muted)]">
              {selectedTreatments.length} treatment{selectedTreatments.length !== 1 ? "s" : ""} selected
            </span>
            <span className="text-base font-bold text-[var(--accent)]">Total: £{total}</span>
          </div>
        )}
      </div>

      {/* Step 2 — Date */}
      {selectedTreatments.length > 0 && (
        <div className="p-6 md:p-8 border-b border-[var(--border-on-light)]">
          <h3 className="text-base font-semibold text-[var(--text-brand)] mb-1 uppercase tracking-wider">
            Step 2 — Choose a clinic date
          </h3>
          <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
            Clinics run once a month. Select a date to see available slots.
          </p>

          {upcomingDates.length === 0 ? (
            <p className="text-sm text-[var(--text-on-light-muted)]">
              No upcoming clinic dates are available right now. Please check back soon or{" "}
              <a href="mailto:nicola@cforganicwellness.com" className="underline text-[var(--text-brand)]">
                email Niki
              </a>
              .
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {upcomingDates.map((d) => (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => setSelectedDate(d.iso)}
                  className={`rounded-xl border px-6 py-4 text-center transition-colors min-w-[100px]
                    ${selectedDate === d.iso
                      ? "border-[var(--text-brand)] bg-[var(--bg-brand-tint)] text-[var(--text-on-light)]"
                      : "border-[var(--border-on-light)] bg-[var(--bg-surface)] text-[var(--text-on-light)] hover:border-[var(--text-brand)]"}`}
                >
                  <p className="text-2xl font-extrabold">{d.day}</p>
                  <p className="text-sm font-semibold">{d.month}</p>
                  <p className="text-xs text-[var(--text-on-light-muted)]">{d.year}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3 — Time slot */}
      {selectedDate && (
        <div className="p-6 md:p-8 border-b border-[var(--border-on-light)]">
          <h3 className="text-base font-semibold text-[var(--text-brand)] mb-1 uppercase tracking-wider">
            Step 3 — Choose a time slot
          </h3>
          <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
            {friendlyDate(selectedDate)} · {durationLabel}
          </p>

          {slotsLoading ? (
            <p className="text-sm text-[var(--text-on-light-muted)]">Checking availability…</p>
          ) : slots.length === 0 ? (
            <p className="text-sm text-[var(--text-on-light-muted)]">
              No slots available for this date. Please choose another date or{" "}
              <a href="mailto:nicola@cforganicwellness.com" className="underline text-[var(--text-brand)]">
                contact Niki
              </a>
              .
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => selectSlot(slot)}
                  aria-pressed={selectedSlotRaw === slot}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition
                    ${selectedSlotRaw === slot
                      ? "bg-[var(--text-brand)] text-[var(--cream)] shadow"
                      : "border border-[var(--border-on-light)] text-[var(--text-on-light)] hover:border-[var(--text-brand)] hover:text-[var(--text-brand)]"}`}
                >
                  {slot}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4 — Details + submit */}
      {selectedSlotRaw && (
        <form onSubmit={handleSubmit} className="p-6 md:p-8">
          <h3 className="text-base font-semibold text-[var(--text-brand)] mb-1 uppercase tracking-wider">
            Step 4 — Your details
          </h3>
          <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
            You&apos;ll be taken to our secure payment page after confirming.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[var(--text-on-light)] mb-1">
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="block w-full rounded-lg border border-[var(--border-on-light)] px-4 py-3 text-sm text-[var(--text-on-light)] bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-on-light)] mb-1">
                Email address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-lg border border-[var(--border-on-light)] px-4 py-3 text-sm text-[var(--text-on-light)] bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
              />
            </div>
          </div>

          <MarketingOptIn checked={marketingOptIn} onChange={setMarketingOptIn} className="mb-6" />

          {/* Booking summary */}
          <div className="rounded-xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] px-5 py-4 mb-6 text-sm text-[var(--text-on-light-muted)] space-y-1">
            <p><span className="text-[var(--text-on-light)]">Date:</span> {friendlyDate(selectedDate!)}</p>
            <p><span className="text-[var(--text-on-light)]">Time:</span> {selectedSlotRaw} ({durationLabel})</p>
            <p><span className="text-[var(--text-on-light)]">Treatments:</span> {selectedTreatments.join(", ")}</p>
            <p className="font-bold text-[var(--accent)]">Total: £{total}</p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto rounded-lg bg-[var(--accent)] px-10 py-4 text-base font-bold text-[var(--on-lead)] shadow-lg hover:bg-[var(--coral-dk)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Please wait…" : `Pay £${total} & Confirm Slot`}
          </button>

          <p className="mt-3 text-xs text-[var(--text-on-light-muted)]">
            You&apos;ll be redirected to our secure SumUp payment page. A calendar invite and confirmation email will
            follow once payment is complete.
          </p>
        </form>
      )}
    </div>
  );
}
